import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { Settings, Coins, ShoppingCart, Check, X } from 'lucide-react';

const UpgradesShop = ({ characterId }) => {
  const [upgradesData, setUpgradesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const loadUpgrades = async () => {
      if (!token) {
        setError('Please log in to view upgrades');
        setLoading(false);
        return;
      }

      if (!characterId) {
        setError('Character ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`https://api.4fingerstudios.com/api/mystveil/upgrades/${characterId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else if (response.status === 404) {
            setError('Character not found');
          } else {
            throw new Error(`Failed to load upgrades: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setUpgradesData(data);
      } catch (err) {
        setError('Failed to load upgrades');
        console.error('Upgrades load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUpgrades();
  }, [token, characterId]);

  const handlePurchaseUpgrade = async (upgradeId) => {
    if (!upgradesData) return;

    setPurchasing(upgradeId);
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/purchase-upgrade', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          characterId: characterId,
          upgradeId: upgradeId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to purchase upgrade: ${response.status}`);
      }

      // Reload upgrades data to reflect the purchase
      const response2 = await fetch(`https://api.4fingerstudios.com/api/mystveil/upgrades/${characterId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response2.ok) {
        const data = await response2.json();
        setUpgradesData(data);
      }
    } catch (err) {
      alert(`Failed to purchase upgrade: ${err.message}`);
    } finally {
      setPurchasing(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Upgrades Shop</h1>
            <p className="mystical-text mb-6">Please log in to view upgrades</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading upgrades...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <Link to="/characters" className="mystical-button">
              Back to Characters
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <h1 className="mystical-title text-black mb-6">
              Upgrades Shop
            </h1>
            
            {/* Character Info */}
            {upgradesData?.character && (
              <div className="mystical-card bg-white bg-opacity-95 mb-6">
                <div className="flex items-center space-x-4">
                  {upgradesData.character.characterImage && (
                    <img 
                      src={upgradesData.character.characterImage} 
                      alt={upgradesData.character.characterName}
                      className="w-16 h-16 rounded-md object-cover"
                    />
                  )}
                  <div>
                    <h2 className="mystical-subtitle text-black text-xl">
                      {upgradesData.character.characterName}
                    </h2>
                    <p className="mystical-text text-gray-600">
                      {upgradesData.character.characterDescription || 'No description provided'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Chip Balance */}
            <div className="mystical-card bg-white bg-opacity-95 mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Coins className="text-yellow-600" size={24} />
                  <h3 className="mystical-subtitle text-black">Available Chips</h3>
                </div>
                <div className="text-right">
                  <p className="mystical-text text-2xl font-bold text-purple-800">
                    {upgradesData?.availableChips || 0}
                  </p>
                  <p className="mystical-text text-sm text-gray-600">chips</p>
                </div>
              </div>
            </div>
          </div>

          {/* Available Upgrades */}
          <div className="mystical-card">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Available Upgrades
            </h3>
            
            {upgradesData?.availableUpgrades?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upgradesData.availableUpgrades.map(upgrade => (
                  <div key={upgrade.id} className="mystical-card bg-white bg-opacity-95">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="mystical-subtitle text-black text-lg mb-2">
                          {upgrade.name}
                        </h4>
                        <p className="mystical-text text-gray-600 text-sm mb-3">
                          {upgrade.description}
                        </p>
                        <div className="flex items-center space-x-2 mb-4">
                          <Coins className="text-yellow-600" size={16} />
                          <span className="mystical-text font-bold text-purple-800">
                            {upgrade.cost} chips
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Settings className="text-gray-500" size={16} />
                        <span className="mystical-text text-sm text-gray-500">
                          {upgrade.category}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => handlePurchaseUpgrade(upgrade.id)}
                        disabled={purchasing === upgrade.id || (upgradesData?.availableChips || 0) < upgrade.cost}
                        className={`mystical-button ${
                          (upgradesData?.availableChips || 0) < upgrade.cost 
                            ? 'opacity-50 cursor-not-allowed' 
                            : ''
                        }`}
                      >
                        {purchasing === upgrade.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Purchasing...
                          </>
                        ) : (upgradesData?.availableChips || 0) < upgrade.cost ? (
                          <>
                            <X className="mr-2" size={16} />
                            Insufficient Chips
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="mr-2" size={16} />
                            Purchase
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <Settings className="mx-auto text-gray-400 mb-4" size={64} />
                <h3 className="mystical-subtitle text-black text-xl mb-4">
                  No Upgrades Available
                </h3>
                <p className="mystical-text text-gray-600 mb-6">
                  This character doesn't have any upgrades available at the moment.
                </p>
                <Link to="/submit" className="mystical-button">
                  Submit Artwork to Earn Chips
                </Link>
              </div>
            )}
          </div>

          {/* Character's Current Upgrades */}
          {upgradesData?.character?.purchasedUpgrades?.length > 0 && (
            <div className="mystical-card mt-8">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Current Upgrades
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upgradesData.character.purchasedUpgrades.map(upgrade => (
                  <div key={upgrade.id} className="mystical-card bg-white bg-opacity-95">
                    <div className="flex items-center space-x-3 mb-2">
                      <Check className="text-green-600" size={20} />
                      <h4 className="mystical-subtitle text-black">
                        {upgrade.name}
                      </h4>
                    </div>
                    <p className="mystical-text text-gray-600 text-sm">
                      {upgrade.description}
                    </p>
                    <p className="mystical-text text-xs text-gray-500 mt-2">
                      Purchased: {new Date(upgrade.purchasedAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/characters" className="mystical-button">
              Back to Characters
            </Link>
            <Link to="/profile" className="mystical-button">
              My Profile
            </Link>
            <Link to="/submit" className="mystical-button">
              Submit Artwork
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UpgradesShop;
